<template>
  <Teleport to="body">
    <Transition name="order-contents-fade">
      <div
        v-if="open"
        class="order-contents-overlay"
        data-qa="basket-order-contents-overlay"
        @click="close"
      />
    </Transition>
    <Transition name="order-contents-slide">
      <aside
        v-if="open"
        class="order-contents-drawer"
        data-qa="basket-order-contents-modal"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="titleId"
      >
        <header class="order-contents-header">
          <h3 class="order-contents-title" :id="titleId">
            {{ $t('ORDER_CONTENT_TEXT') }}
          </h3>
          <button
            ref="closeBtn"
            class="order-contents-close"
            type="button"
            data-qa="basket-order-contents-close-btn"
            :aria-label="$t('close')"
            @click="close"
          >
            <svg
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M2.46967 12.4697C2.17678 12.7626 2.17678 13.2374 2.46967 13.5303C2.76256 13.8232 3.23744 13.8232 3.53033 13.5303L7.99999 9.06067L12.4697 13.5304C12.7626 13.8232 13.2374 13.8232 13.5303 13.5304C13.8232 13.2375 13.8232 12.7626 13.5303 12.4697L9.06065 8.00001L13.5303 3.53033C13.8232 3.23744 13.8232 2.76256 13.5303 2.46967C13.2374 2.17678 12.7626 2.17678 12.4697 2.46967L7.99999 6.93935L3.53033 2.46969C3.23744 2.1768 2.76256 2.1768 2.46967 2.46969C2.17678 2.76258 2.17678 3.23746 2.46967 3.53035L6.93933 8.00001L2.46967 12.4697Z"
                fill="currentColor"
              />
            </svg>
          </button>
        </header>
        <div class="order-contents-body">
          <p
            class="order-contents-counter"
            data-qa="basket-order-contents-counter"
          >
            {{ counterText }}
          </p>
          <ul class="order-contents-list" data-qa="basket-order-contents-list">
            <li
              v-for="item in items"
              :key="item.article"
              class="order-contents-item"
              :data-qa="'basket-order-contents-item-' + item.article"
            >
              <SummaryItemThumb
                class="order-contents-thumb"
                :item="item"
                qa-prefix="basket-order-contents-thumb"
              />
              <div class="order-contents-name">
                {{ item.name || item.article }}
              </div>
              <div class="item-price" />
            </li>
          </ul>
        </div>
        <footer class="order-contents-footer">
          <span>{{ $t('summary_add_more_products') }}</span>
          <a
            class="order-contents-link"
            href="/basket/"
            data-qa="basket-order-contents-back-to-cart-link"
            >{{ $t('back_to_cart_button') }}</a
          >
        </footer>
      </aside>
    </Transition>
  </Teleport>
</template>

<script setup>
import { nextTick, onBeforeUnmount, ref, useId, watch } from 'vue'
import SummaryItemThumb from './SummaryItemThumb.vue'

const props = defineProps({
  open: {
    type: Boolean,
    default: false,
  },
  items: {
    type: Array,
    default: () => [],
  },
  counterText: {
    type: String,
    default: '',
  },
})

const emit = defineEmits(['close'])

const titleId = 'basket-order-contents-title-' + useId()
const closeBtn = ref(null)

let locked = false
let prevBodyOverflow = ''
let returnFocusEl = null

const close = () => {
  emit('close')
}

const onKeydown = (event) => {
  if (event.key === 'Escape') {
    close()
  }
}

const lock = () => {
  if (locked) return
  locked = true
  prevBodyOverflow = document.body.style.overflow
  document.body.style.overflow = 'hidden'
  document.addEventListener('keydown', onKeydown)
}

const unlock = () => {
  if (!locked) return
  locked = false
  document.body.style.overflow = prevBodyOverflow
  document.removeEventListener('keydown', onKeydown)
}

const captureFocus = () => {
  returnFocusEl = document.activeElement
  nextTick(() => {
    closeBtn.value?.focus()
  })
}

const restoreFocus = () => {
  const el = returnFocusEl
  returnFocusEl = null
  if (el && el !== document.body && document.contains(el)) {
    el.focus()
  }
}

watch(
  () => props.open,
  (value) => {
    if (value) {
      lock()
      captureFocus()
    } else {
      unlock()
      restoreFocus()
    }
  },
  { immediate: true }
)

onBeforeUnmount(() => {
  unlock()
  restoreFocus()
})
</script>

<style lang="scss" scoped>
.order-contents-overlay {
  position: fixed;
  inset: 0;
  background: rgba(53, 81, 107, 0.18);
  z-index: 100001;
}

.order-contents-drawer {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  max-height: 85vh;
  background: #fff;
  border-radius: 16px 16px 0 0;
  z-index: 100002;
}

.order-contents-header {
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  padding: 16px 16px 12px;
  border-bottom: 1px solid #e5ecf1;
}

.order-contents-title {
  flex: 1;
  min-width: 0;
  margin: 0;
  color: #39444c;
  font-size: 18px;
  line-height: 24px;
  font-weight: 500;
  letter-spacing: -0.014em;
}

.order-contents-close {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  padding: 0;
  border: 0;
  background: none;
  color: #39444c;
  cursor: pointer;

  svg {
    width: 24px;
    height: 24px;
  }
}

.order-contents-body {
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 16px 16px 0;
}

.order-contents-counter {
  margin: 0;
  padding-bottom: 12px;
  color: #39444c;
  font-size: 16px;
  line-height: 24px;
  font-weight: 400;
  letter-spacing: -0.014em;
}

.order-contents-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.order-contents-item {
  display: flex;
  align-items: center;
  gap: 16px;
  min-height: 64px;
}

.order-contents-item .order-contents-thumb {
  flex: 0 0 74px;
  width: 74px;
  height: 64px;
}

.order-contents-name {
  flex: 1;
  min-width: 0;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  overflow: hidden;
  color: #39444c;
  font-size: 16px;
  line-height: 20px;
  font-weight: 500;
  letter-spacing: -0.014em;
}

.item-price {
  flex-shrink: 0;
  white-space: nowrap;
  text-align: right;
  color: #39444c;
  font-size: 16px;
  line-height: 24px;
  font-weight: 400;
  letter-spacing: -0.014em;
}

.order-contents-footer {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  flex-shrink: 0;
  padding: 16px;
  color: #000;
  font-size: 14px;
  line-height: 20px;
  font-weight: 400;
  letter-spacing: -0.014em;
}

.order-contents-link {
  color: #004c50;
  text-decoration: underline;
  font-size: 14px;
  line-height: 20px;
  font-weight: 400;
  letter-spacing: -0.014em;
}

.order-contents-fade-enter-active,
.order-contents-fade-leave-active {
  transition: opacity 0.25s ease;
}

.order-contents-fade-enter-from,
.order-contents-fade-leave-to {
  opacity: 0;
}

.order-contents-slide-enter-active,
.order-contents-slide-leave-active {
  transition: transform 0.25s ease;
}

.order-contents-slide-enter-from,
.order-contents-slide-leave-to {
  transform: translateY(100%);
}

@media (min-width: 1024px) {
  .order-contents-drawer {
    top: 0;
    left: auto;
    width: 400px;
    max-width: 100vw;
    height: 100vh;
    max-height: none;
    padding: 24px;
    border-radius: 0;
    box-shadow:
      0 16px 24px -4px rgba(4, 8, 13, 0.12),
      0 4px 8px -2px rgba(4, 8, 13, 0.12);
  }

  .order-contents-header {
    position: static;
    justify-content: space-between;
    min-height: 28px;
    margin-bottom: 32px;
    padding: 0 40px 0 0;
    border-bottom: 0;
  }

  .order-contents-title {
    font-size: 20px;
    line-height: 28px;
    font-weight: 600;
    letter-spacing: -0.014em;
  }

  .order-contents-close {
    position: absolute;
    top: 24px;
    right: 24px;
    width: 28px;
    height: 28px;
    color: rgba(9, 22, 32, 0.8);

    svg {
      width: 20px;
      height: 20px;
    }
  }

  .order-contents-body {
    padding: 0;
  }

  .order-contents-footer {
    padding: 16px 0 0;
  }

  .order-contents-slide-enter-from,
  .order-contents-slide-leave-to {
    transform: translateX(100%);
  }
}
</style>
